import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/lib/api";
import { authHeaders } from "@/lib/session";
import { useUser } from "@/store/user";
import { CertificateLink } from "@/components/CertificateLink";
import usePageTitle from "@/hooks/usePageTitle";

const STATUS_META = {
  available: {
    label: "متاح الآن",
    color: "#2ec48e",
    bg: "rgba(46, 196, 142, 0.10)",
    border: "rgba(46, 196, 142, 0.40)",
  },
  in_development: {
    label: "قيد التطوير",
    color: "#f5c87a",
    bg: "rgba(245, 200, 122, 0.12)",
    border: "rgba(245, 200, 122, 0.45)",
  },
  soon: {
    label: "قريبًا",
    color: "#8b6df0",
    bg: "rgba(139, 109, 240, 0.12)",
    border: "rgba(139, 109, 240, 0.42)",
  },
};

function StatusPill({ status, testid }) {
  const m = STATUS_META[status] || STATUS_META.soon;
  return (
    <span
      className="status-pill"
      data-testid={testid}
      style={{ color: m.color, background: m.bg, borderColor: m.border }}
    >
      {m.label}
    </span>
  );
}

function ProgressBar({ pct, accent }) {
  return (
    <div className="track-progress" aria-label={`التقدّم ${pct}%`}>
      <div
        className="track-progress-fill"
        style={{ width: `${pct}%`, background: accent }}
      />
    </div>
  );
}

function UnitBlock({ unit, accent }) {
  const navigate = useNavigate();
  return (
    <div className="track-unit" data-testid={`unit-${unit.id}`}>
      <div className="track-unit-title">{unit.title}</div>
      <div className="track-unit-lessons">
        {unit.lessons.map((l, idx) => (
          <button
            key={l.id}
            className={`unit-lesson ${l.completed ? "done" : ""}`}
            onClick={() => navigate(`/lesson/${l.id}`)}
            data-testid={`unit-lesson-${l.id}`}
            style={{ "--accent": accent }}
          >
            <span className="lesson-idx">{idx + 1}</span>
            <span className="lesson-meta">
              <span className="lesson-title">{l.title}</span>
              <span className="lesson-sub">
                <span>{l.badge?.icon}</span>
                <span>{l.skill}</span>
                <span>·</span>
                <span>{l.duration} د</span>
              </span>
            </span>
            <span className="lesson-state" aria-hidden>
              {l.completed ? "✓" : "←"}
            </span>
          </button>
        ))}
        {unit.test_id && (
          <button
            className={`unit-test ${unit.test_passed ? "passed" : ""}`}
            onClick={() => navigate(`/test/${unit.test_id}`)}
            data-testid={`unit-test-${unit.id}`}
            style={{ "--accent": accent }}
          >
            <span className="lesson-idx">📝</span>
            <span className="lesson-meta">
              <span className="lesson-title">اختبار الوحدة</span>
              <span className="lesson-sub">
                {unit.test_passed
                  ? "تمّ الاجتياز ✓ — يمكنك الإعادة"
                  : "أنجِز دروس الوحدة ثمّ اختبر فهمك"}
              </span>
            </span>
            <span className="lesson-state" aria-hidden>
              {unit.test_passed ? "✓" : "→"}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

function PremiumPill({ unlocked, testid }) {
  return (
    <span
      className="status-pill"
      data-testid={testid}
      style={{
        color: unlocked ? "#2ec48e" : "#c79c6a",
        background: unlocked
          ? "rgba(46,196,142,0.12)"
          : "rgba(199,156,106,0.12)",
        borderColor: unlocked
          ? "rgba(46,196,142,0.45)"
          : "rgba(199,156,106,0.45)",
      }}
    >
      {unlocked ? "💎 Premium ✓" : "💎 Premium"}
    </span>
  );
}

function TrackCard({ track, expanded, onToggle }) {
  const navigate = useNavigate();
  const m = STATUS_META[track.status] || STATUS_META.soon;
  const isAvail = track.status === "available";
  const isPremium = !!track.is_premium;
  const premiumLocked = !!track.locked;

  return (
    <article
      className={`track-card status-${track.status}`}
      data-testid={`track-${track.id}`}
      style={{ "--accent": track.accent }}
    >
      <header
        className="track-head"
        onClick={() => isAvail && onToggle(track.id)}
        role={isAvail ? "button" : undefined}
        tabIndex={isAvail ? 0 : -1}
      >
        <div className="track-icon" aria-hidden style={{ background: track.accent }}>
          {track.icon}
        </div>
        <div className="track-info">
          <div className="track-row1">
            <span className="track-code">{track.code}</span>
            <h3 className="track-title">{track.title}</h3>
            <StatusPill status={track.status} testid={`status-${track.id}`} />
            {isPremium && (
              <PremiumPill
                unlocked={!premiumLocked}
                testid={`premium-${track.id}`}
              />
            )}
          </div>
          <div className="track-subtitle">{track.subtitle}</div>
          <div className="track-meta">
            <span>📊 {track.level_label}</span>
            <span>·</span>
            <span>⏱ {track.duration_label}</span>
            {isAvail && (
              <>
                <span>·</span>
                <span>
                  📚 {track.stats?.total_lessons} درسًا · {(track.units?.length ?? 0)} وحدات
                </span>
              </>
            )}
          </div>
          {isAvail && track.stats?.total_lessons > 0 && (
            <>
              <ProgressBar pct={track.stats?.progress_pct} accent={track.accent} />
              <div className="track-progress-label">
                {track.stats?.completed_lessons}/{track.stats?.total_lessons} مكتمل
                ({track.stats?.progress_pct}%)
              </div>
            </>
          )}
        </div>
        {isAvail && (
          <div className="track-arrow" aria-hidden>
            {expanded ? "▲" : "▼"}
          </div>
        )}
      </header>

      {/* Body: description + outcomes + units (expanded) */}
      {(expanded || !isAvail) && (
        <div className="track-body" data-testid={`body-${track.id}`}>
          <p className="track-desc">{track.description}</p>

          {premiumLocked && isAvail && (
            <div
              data-testid={`premium-lock-${track.id}`}
              style={{
                margin: "12px 0 18px",
                padding: "14px 16px",
                background: "rgba(199,156,106,0.10)",
                border: "1px solid rgba(199,156,106,0.40)",
                borderRadius: 12,
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontSize: 28 }}>🔒</div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <strong style={{ color: "#c79c6a" }}>
                  هذا المسار ضمن Premium
                </strong>
                <p
                  className="muted"
                  style={{
                    margin: "4px 0 0",
                    fontSize: 13,
                    lineHeight: 1.7,
                  }}
                >
                  افتح كلّ الدروس والاختبارات والشهادة بتجربة 7 أيام مجّانيّة
                  أو اشتراك يبدأ من 29 ر.س / شهر.
                </p>
              </div>
              <button
                className="btn btn-gold"
                onClick={() => navigate(`/pricing?from=${track.id}`)}
                data-testid={`unlock-cta-${track.id}`}
                style={{
                  background: "#c79c6a",
                  color: "#1a1530",
                  fontWeight: 700,
                  padding: "10px 16px",
                }}
              >
                افتح Premium →
              </button>
            </div>
          )}

          {track.outcomes?.length > 0 && (
            <div className="track-outcomes">
              <h4>المخرجات التعليميّة:</h4>
              <ul>
                {track.outcomes.map((o, i) => (
                  <li key={i}>{o}</li>
                ))}
              </ul>
            </div>
          )}

          {isAvail ? (
            <div className="track-units">
              <h4>الوحدات والدروس:</h4>
              {track.units.map((u) => (
                <UnitBlock key={u.id} unit={u} accent={track.accent} />
              ))}
              {track.final_test_id && (
                <div className="track-final-test" data-testid={`final-test-${track.id}`}>
                  <button
                    className={`unit-test final ${track.final_test_passed ? "passed" : ""}`}
                    onClick={() => navigate(`/test/${track.final_test_id}`)}
                    style={{ "--accent": track.accent }}
                  >
                    <span className="lesson-idx">🎓</span>
                    <span className="lesson-meta">
                      <span className="lesson-title">الاختبار النهائيّ للمسار</span>
                      <span className="lesson-sub">
                        {track.final_test_passed
                          ? "تمّ الاجتياز — استلم شهادتك ↓"
                          : "أكمل الوحدات لاجتياز المسار وكسب الشهادة"}
                      </span>
                    </span>
                    <span className="lesson-state" aria-hidden>
                      {track.final_test_passed ? "✓" : "→"}
                    </span>
                  </button>
                </div>
              )}
              {track.certificate_eligible && (
                <CertificateLink
                  trackId={track.id}
                  className="btn btn-gold cta-primary track-cert-btn"
                  testId={`cert-${track.id}`}
                  style={{ marginTop: 14, display: "inline-flex" }}
                >
                  🏅 تحميل شهادة إتمام المسار (PDF)
                </CertificateLink>
              )}
            </div>
          ) : (
            <div className="track-soon-banner" data-testid={`soon-${track.id}`}>
              <span className="track-soon-mark" style={{ color: m.color }}>
                ⏳
              </span>
              <div>
                <strong style={{ color: m.color }}>{m.label}</strong>
                <p>
                  هذا المسار قيد التحضير. سيُتاح في المراحل القادمة من
                  تطوير المنصّة. ابقَ معنا!
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function TracksPage() {
  const navigate = useNavigate();
  usePageTitle("المسارات التعليميّة · بَلاغة");
  const { userId, progressVersion, isAuthed } = useUser();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all"); // all | available | soon
  const [expanded, setExpanded] = useState(null);

  // Refetch when:
  //   • user identity changes (login/logout, anon→authed merge)
  //   • local progressVersion bumps (lesson completed somewhere in the app)
  // We deliberately key on userId + version rather than
  // completed_lessons.length, which can be stable across identity swaps.
  const loadTracks = () => {
    setLoading(true);
    setErr("");
    fetch(`${API}/tracks`, { credentials: "include", headers: authHeaders() })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => (Array.isArray(d?.tracks) ? d.tracks : []))
      .then(setTracks)
      .catch((e) => {
        setTracks([]);
        setErr(e?.message || "تعذّر تحميل المسارات");
      })
      .finally(() => setLoading(false));
  };
  useEffect(loadTracks, [userId, progressVersion, isAuthed]);

  // Auto-expand first available track with progress once tracks load
    useEffect(() => {
    if (!tracks.length || expanded !== null) return;
    const first = tracks.find(
      (t) => t.status === "available" && (t.stats?.total_lessons ?? 0) > 0
    );
    if (first) setExpanded(first.id);
  }, [tracks, expanded]);

  const filtered = tracks.filter((t) => {
    if (filter === "all") return true;
    if (filter === "available") return t.status === "available";
    if (filter === "soon") return t.status === "soon" || t.status === "in_development";
    return true;
  });

  const counts = {
    all: tracks.length,
    available: tracks.filter((t) => t.status === "available").length,
    soon: tracks.filter((t) => t.status === "soon" || t.status === "in_development").length,
  };

  return (
    <div className="shell has-bottom-nav tracks-page" data-testid="tracks-page">
      <div className="page-header">
        <h1 data-testid="tracks-title">
          المسارات <span className="gold">التعليميّة</span>
        </h1>
        <p>
          خارطة منهج بَلاغة الشاملة — من تأسيس المفاهيم البلاغيّة إلى
          الاحتراف، مرورًا بالبلاغة القرآنيّة والتذوّق الأدبيّ. اختر مسارك
          وابدأ رحلتك.
        </p>
      </div>

      {/* Filter chips */}
      <div className="tracks-filters" data-testid="tracks-filters">
        {[
          { id: "all", label: "الكلّ", count: counts.all },
          { id: "available", label: "متاح الآن", count: counts.available },
          { id: "soon", label: "قريبًا", count: counts.soon },
        ].map((f) => (
          <button
            key={f.id}
            className={`tracks-filter ${filter === f.id ? "active" : ""}`}
            onClick={() => setFilter(f.id)}
            data-testid={`filter-${f.id}`}
          >
            {f.label}
            <span className="filter-count">{f.count}</span>
          </button>
        ))}
      </div>

      {/* Legend */}
      <div className="tracks-legend" data-testid="tracks-legend">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <span key={k} className="legend-item">
            <span
              className="legend-dot"
              style={{ background: m.color, boxShadow: `0 0 8px ${m.color}55` }}
            />
            {m.label}
          </span>
        ))}
      </div>

      {err && (
        <div
          className="admin-error"
          data-testid="tracks-error"
          style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-start" }}
        >
          <span>⚠️ {err}</span>
          <button
            className="btn btn-ghost"
            onClick={loadTracks}
            data-testid="tracks-retry"
            style={{ fontSize: 13 }}
          >
            إعادة المحاولة
          </button>
        </div>
      )}

      {loading ? (
        <div className="admin-empty">جارٍ تحميل المسارات…</div>
      ) : filtered.length === 0 ? (
        <div className="admin-empty">لا توجد مسارات تطابق التصفية.</div>
      ) : (
        <div className="tracks-grid" data-testid="tracks-grid">
          {filtered.map((t) => (
            <TrackCard
              key={t.id}
              track={t}
              expanded={expanded === t.id}
              onToggle={(id) => setExpanded(expanded === id ? null : id)}
            />
          ))}
        </div>
      )}

      <div className="about-cta" style={{ marginTop: 28 }}>
        <button
          className="btn btn-ghost"
          onClick={() => navigate("/curriculum")}
          data-testid="tracks-to-curriculum"
        >
          📊 عرض كلّ الدروس بشكل قائمة
        </button>
        <button
          className="btn btn-ghost"
          onClick={() => navigate("/about")}
          data-testid="tracks-to-about"
        >
          ℹ️ عن المشروع
        </button>
      </div>
    </div>
  );
}

